import styled, { css } from 'styled-components';
import { em } from 'styles';

import { Button } from 'components/_shared/Button';
import { typographyStyle } from '../_shared/Typography';
import { Select } from '../_shared/Select';

export const StyledButton = styled(Button)`
  ${({ theme, isActive }) => css`
    ${typographyStyle({ theme, variant: 'body2' })};
    display: inline-block;
    border-radius: 20px;
    padding: ${em(6)} ${em(12)};
    font-size: 14px;
    ${isActive &&
    css`
      background: ${theme.colors.primary.p3};
      color: ${theme.colors.white};
    `};
    &:hover {
      background: ${theme.colors.primary.p1};
      color: ${theme.colors.white} !important;
    }
    &:disabled {
      background: ${theme.colors.nature.n3};
      color: ${theme.colors.black} !important;
    }
    &:disabled:hover {
      background: ${theme.colors.nature.n3} !important;
      color: ${theme.colors.black} !important;
    }
  `}
`;

export const StartButton = styled(StyledButton)`
  ${({ theme, isActive }) => css`
    padding: ${em(6)} ${em(12)};
    display: flex;
    ${isActive &&
    css`
      background: ${theme.colors.primary.p3};
      color: ${theme.colors.white};
    `};
    &:hover {
      background: ${theme.colors.primary.p1};
      color: ${theme.colors.white} !important;
    }
  `}
`;

export const RemoveButton = styled(StyledButton)`
  ${() => css`
    margin-left: ${em(50)};
  `}
`;

export const Wrapper = styled.div`
  ${({ theme }) => css`
    border-top-right-radius: 50px;
    border-top-left-radius: 50px;
    background: ${theme.colors.white};
  `}
`;

export const UploadInfo = styled.div`
  ${({ theme }) => css`
    padding: ${em(20)};
    background: ${theme.colors.white};
    margin: 0 auto;
    text-align: center;
    color: ${theme.colors.nature.n3};
  `}
`;

export const UploadFileItem = styled(Wrapper)`
  ${({ theme }) => css`
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    padding: ${em(5)} ${em(20)};
    background: ${theme.colors.white};
    align-items: baseline;
    justify-content: space-between;
  `}
`;

export const UploadFiles = styled(Wrapper)`
  ${({ theme }) => css`
    padding: ${em(20)} 0;
    background: ${theme.colors.white};
  `}
`;

export const UploadFileItemInfo = styled(Wrapper)`
  ${({ theme }) => css`
    display: flex;
    background: ${theme.colors.white};
    align-items: center;
    justify-content: space-between;
  `}
`;

export const StyledSelect = styled(Select)`
  text-align: start;
`;

export const ButtonContainer = styled(Wrapper)`
  ${({ theme }) => css`
    display: flex;
    background: ${theme.colors.white};
    gap: ${em(20)} ${em(50)};
    padding: 0 ${em(20)};
    & > * {
      flex: 1 ${em(150)};
      max-width: ${em(150)};
      min-width: ${em(150)};
    }
  `}
`;

export const TitleContainer = styled(Wrapper)`
  ${({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    text-align: center;
    border-top-right-radius: 50px;
    border-top-left-radius: 50px;
    background: ${theme.colors.white};
  `}
`;

export const FileContainer = styled(Wrapper)`
  ${({ theme }) => css`
    padding: ${em(16)};
    min-width: 30%;
    border-radius: 12px;
    background: ${theme.colors.white};
    font-family: sans-serif;
    align-items: center;
    border: 1px solid ${theme.colors.nature.n2};
    margin-bottom: ${em(20)};
    margin-top: ${em(20)};
    box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.2s ease, transform 0.2s ease;
  `}
`;

export const UploadContainer = styled(Wrapper)`
  ${({ theme }) => css`
    background: ${theme.colors.white};
    display: flex;
    flex-direction: column;
    font-family: sans-serif;
  `}
`;

export const ProgressBarItem = styled(Wrapper)`
  ${({ theme }) => css`
    background: ${theme.colors.white};
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    font-family: sans-serif;
    align-items: center;
  `}
`;

export const Title = styled.h2`
  ${({ theme }) => css`
    ${typographyStyle({ theme, variant: 'body2' })};
    margin-right: ${em(12)};
    font-size: ${em(20)};
    color: ${theme.colors.black};
    font-weight: 600;
  `}
`;

export const HelpText = styled.p`
  ${({ theme, maxWidth }) => css`
    ${typographyStyle({ theme, variant: 'body2' })};
    margin-right: ${em(12)};
    padding: ${em(5)} 0;
    display: inline-block;
    max-width: ${maxWidth ? maxWidth : 300}px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
  `}
`;

export const WarningText = styled(HelpText)`
  ${({ theme }) => css`
    color: ${theme.colors.danger};
    max-width: none;
    white-space: normal;
  `}
`;

export const ProgressBarContainer = styled.div`
  ${({ theme }) => css`
    width: 100%;
    min-width: ${em(100)};
    height: 24px;
    position: relative;
    background-color: ${theme.colors.primary.p4};
    overflow: hidden;
    border-radius: 8px;
    border: 1px solid ${theme.colors.primary.p1};
    text-align: center;
  `}
`;

export const ProgressBarFill = styled.div`
  ${({ theme, $width, $status }) => css`
    height: 100%;
    background-color: ${theme.colors.primary.p2};
    width: ${$width}%;
    ${$status === 'DONE' &&
    css`
      background-color: ${theme.colors.done};
    `};
    ${$status === 'FAILED' &&
    css`
      background-color: ${theme.colors.failed};
    `};
    ${$status === 'PLANNED' &&
    css`
      background-color: ${theme.colors.primary.p4};
    `};
    ${$status === 'RUNNING' &&
    css`
      background: repeating-linear-gradient(
        -45deg,
        ${theme.colors.primary.p2} 0 10px,
        #0000 0 15px
      );
      background-size: 40px 100%;
      animation: l3 4s infinite linear;
      @keyframes l3 {
        100% {
          background-position: right;
        }
      }
    `};
    transition: width 0.2s ease-out;
  `}
`;

export const ProgressBarText = styled(HelpText)`
  ${() => css`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
  `}
`;

export const UploadLabel = styled.label`
  ${({ theme }) => css`
    ${typographyStyle({ theme, variant: 'body2' })};
    padding: ${em(6)} ${em(12)};
    display: flex;
    gap: ${em(5)};
    cursor: pointer;
    align-items: center;
    border: 1px solid ${theme.colors.primary.p1};
    border-radius: 20px;
    background-color: ${theme.colors.primary.p1};
    color: ${theme.colors.white};
    font-size: 14px;
    transition: background 0.2s ease;
    &:hover {
      background: ${theme.colors.primary.p4};
      color: ${theme.colors.black};
    }
  `}
`;
